import React, { useMemo } from "react"
import { Spinner } from "reactstrap"
import { EngDiagQuestionView } from "./EngDiagQuestionView"
import {
  fetchEngSkillBuilderAudio,
  getEngSkillBuilderQuestion,
  getEngSkillBuilderState,
  skipEngSkillBuilderQuestion,
  submitEngSkillBuilderAnswer,
  type EngSkillBuilderRef,
  type EngSkillBuilderState,
} from "../services/eng-skillbuilder.service"
import { EngAudioFetcherContext } from "./eng-diag-audio-context"
import {
  useEngAttemptRunner,
  type EngAttemptTransport,
} from "../hooks/useEngAttemptRunner"
import {
  ENG_SB_COPY as COPY,
  SHOW_SKILL_PROVENANCE,
} from "../../../constants/diagnostics/eng-diag.constants"
import styles from "./eng-diag-player.module.css"
import theme from "./eng-diag-theme.module.css"

type Props = {
  /** The lesson activity the student opened. */
  activityKey: string
  /** Called once the last question is answered, so the page can show the finish screen. */
  onFinished?: (state: EngSkillBuilderState) => void
}

/**
 * Takes a student through an English Skill Builder: every question written for one skill, one at
 * a time, with the verdict after each.
 *
 * The question-serving loop is useEngAttemptRunner, shared with the diagnostics player - same
 * machinery, same hard-won fixes. Two things differ here and both are deliberate:
 *
 *   THE CHILD IS TOLD HOW THEY DID. `holdFeedback` keeps the question on screen after submitting
 *   so the verdict can be read, and Next moves on. A diagnostic measures and so stays silent;
 *   practice without feedback is just more of the thing that did not work.
 *
 *   THE PILL NAMES THE SKILL, not the cluster. A Skill Builder IS one skill, the child knows
 *   they are practising it, and hiding it would make the activity look arbitrary. The YEAR is
 *   still never shown - the state does not even carry it.
 */
export function EngSkillBuilderPlayer({ activityKey, onFinished }: Props) {
  // Memoised, and it has to be: the runner's load() depends on the transport and its first
  // effect depends on load, so a transport rebuilt every render is a refetch loop.
  const transport: EngAttemptTransport<EngSkillBuilderState> = useMemo(() => {
    const ref: EngSkillBuilderRef = { activity_key: activityKey }
    return {
      getState: signal => getEngSkillBuilderState(ref, signal),
      readProgress: state => ({
        answered: state.answered_count ?? 0,
        total: state.question_count ?? 0,
      }),
      readCurrentKey: state => state.current_question_key,
      getQuestion: (questionKey, signal) =>
        getEngSkillBuilderQuestion({ ...ref, sb_question_key: questionKey }, signal),
      submitAnswer: ({ questionKey, response, durationSeconds }) =>
        submitEngSkillBuilderAnswer({
          ...ref,
          sb_question_key: questionKey,
          response,
          duration_seconds: durationSeconds,
        }),
      skipQuestion: ({ questionKey, reason, durationSeconds }) =>
        skipEngSkillBuilderQuestion({
          ...ref,
          sb_question_key: questionKey,
          skip_reason: reason,
          duration_seconds: durationSeconds,
        }),
    }
  }, [activityKey])

  const {
    loading,
    busy,
    error,
    state,
    question,
    answers,
    setAnswers,
    canSubmit,
    answerParts,
    secondsLeft,
    progress,
    feedback,
    submit,
    skip,
    continueAfterFeedback,
  } = useEngAttemptRunner<EngSkillBuilderState>({
    transport,
    copy: COPY,
    onFinished,
    holdFeedback: true,
  })

  if (loading) {
    return (
      <div className={`${theme.engDiagTheme} text-center py-5`}>
        <Spinner color="primary" />
        <div className={`mt-3 ${styles.engDiagNoticeBody}`}>{COPY.LOADING}</div>
      </div>
    )
  }

  if (error && !question) {
    return (
      <div className={`${theme.engDiagTheme} ${styles.engDiagNotice}`}>
        <p className={styles.engDiagError}>{error}</p>
      </div>
    )
  }

  if (!question) {
    return (
      <div className={`${theme.engDiagTheme} ${styles.engDiagNotice}`}>
        <div className={styles.engDiagNoticeTitle}>{COPY.FINISH_TITLE}</div>
        <p className={styles.engDiagNoticeBody}>
          {COPY.FINISH_NOTE}
          {state && state.awaiting_marking_count > 0
            ? ` ${COPY.FINISH_PENDING_NOTE}`
            : ""}
        </p>
      </div>
    )
  }

  const { answered, total } = progress
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0
  const skillName = state?.skill_name || null
  // The last question, so the button after the verdict says Finish rather than Next and a child
  // is not promised a question that does not exist.
  const isLast = !!feedback && !feedback.next_question_key

  const verdict = feedback
    ? feedback.awaiting_marking || feedback.is_correct === null
      ? { title: COPY.PENDING_TITLE, body: COPY.PENDING_BODY, tone: styles.engDiagVerdictPending }
      : feedback.is_correct
        ? { title: COPY.CORRECT_TITLE, body: COPY.CORRECT_BODY, tone: styles.engDiagVerdictRight }
        : { title: COPY.INCORRECT_TITLE, body: COPY.INCORRECT_BODY, tone: styles.engDiagVerdictWrong }
    : null

  return (
    // Every Listen button below this reads its clip from the Skill Builder endpoint. Without
    // this the shared content renderer would ask the diagnostics one, which cannot see an
    // 'eng-aq-question' clip and answers with nothing.
    <EngAudioFetcherContext.Provider value={fetchEngSkillBuilderAudio}>
    <div className={`${theme.engDiagTheme} assessement-section summary-width-hold`}>
      <div className={styles.engDiagCard}>
        <div className={styles.engDiagCardHead}>
          {/* The skill, which unlike the diagnostic IS shown: a Skill Builder is one skill and
              the child is meant to know which. Still no year - the state does not carry one. */}
          {skillName ? (
            <span className={styles.engDiagClusterPill}>
              <i className="fas fa-dumbbell" aria-hidden="true" />
              {skillName}
            </span>
          ) : null}

          {/* TEMPORARY review aid - see SHOW_SKILL_PROVENANCE. It names the YEAR, which is why
              it is off for real students. */}
          {SHOW_SKILL_PROVENANCE && (question.provenance || question.id) ? (
            <div className={styles.engDiagProvenance}>
              {/* Curriculum order, outside in, with the skill LAST: the path is context and
                  the skill is the thing actually being read. Leading with the skill made the
                  line look scrambled - the PM read
                  "...dictated words - 3 · Year 4 · Spelling · Statutory word lists · [Y3/4
                  list] Part 3" as a shuffle, when every part was in its right place.
                  `topic` is dropped: the subtopic names it closely enough, and the line has to
                  sit beside the progress bar without wrapping. */}
              {question.provenance ? (
                <>
                <span className="text-muted">
                  {[
                    question.provenance.year,
                    question.provenance.cluster,
                    question.provenance.subtopic,
                  ]
                    .filter(Boolean)
                    .map(part => `${part} · `)
                    .join("")}
                </span>
                <span className="fw-700">{question.provenance.skill}</span>
                </>
              ) : null}
              {/* Last, and quiet: it is for whoever is reading over the child's
                  shoulder, not for the child. `select-all` because a 12-character mixed-case
                  key is the kind of string that gets copied wrong by hand. */}
              {question.id ? (
                <span className={styles.engDiagQuestionId} title="Question ID">
                  {question.id}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className={styles.engDiagProgress}>
            <span className={styles.engDiagProgressCount}>
              <span className={styles.engDiagProgressCountValue}>{answered}</span>/{total}
              {/* By COUNT, never by position: a skipped question comes back, so "question 3 of
                  10" would go backwards. */}
              <span className={styles.engDiagProgressQuestion}>
                {COPY.QUESTION_LABEL_SHORT}
                {Math.min(answered + (feedback ? 0 : 1), Math.max(total, 1))}
              </span>
            </span>
            <div
              className={styles.engDiagProgressTrack}
              role="progressbar"
              aria-label={`${answered} of ${total} ${COPY.ANSWERED_SUFFIX}`}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={styles.engDiagProgressFill}
                style={{ width: `${Math.max(percent, 1)}%` }}
              />
            </div>
            {secondsLeft !== null && (
              <span
                className={`${styles.engDiagTimer} ${
                  secondsLeft <= 10 ? styles.engDiagTimerLow : ""
                }`}
              >
                {secondsLeft}s
              </span>
            )}
          </div>
        </div>

        <EngDiagQuestionView
          question={question}
          value={answers}
          onChange={setAnswers}
          // The intent while the verdict is up: the answer is already recorded and cannot
          // change. NOTE the view currently ignores this prop (`void readOnly` in
          // EngDiagQuestionView), so the fields stay editable for now - harmless, because
          // `answers` is discarded when the next question loads, but it does look wrong. Passed
          // anyway so the Skill Builder gets the lock for free whenever that prop is honoured.
          readOnly={!!feedback}
        />

        {error && <p className={styles.engDiagError}>{error}</p>}

        {verdict ? (
          <div className={`${styles.engDiagVerdict} ${verdict.tone}`} role="status">
            <div className={styles.engDiagVerdictTitle}>{verdict.title}</div>
            <p className={styles.engDiagVerdictBody}>{verdict.body}</p>
            <button
              type="button"
              className={styles.engDiagSubmit}
              onClick={() => void continueAfterFeedback()}
              disabled={busy}
            >
              {isLast ? COPY.SEE_RESULTS : COPY.NEXT_QUESTION}
            </button>
          </div>
        ) : (
          <div className={styles.engDiagFooter}>
            <button
              type="button"
              className={styles.engDiagSkip}
              onClick={() => void skip("MANUAL")}
              disabled={busy}
            >
              {COPY.SKIP_FOR_NOW}
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
            {/* An empty submission is marked WRONG, so letting it through costs a child a mark
                for a mis-click. "Skip for now" is the way past one they cannot do. */}
            <div className={styles.engDiagSubmitRow}>
              {!canSubmit && (
                <span className={styles.engDiagSubmitHint}>
                  {/* Same wording as the diagnostic: submit waits for every part, so a
                      part-finished question says which are left rather than leaving the
                      button grey with no reason. */}
                  {answerParts.answered > 0 && answerParts.total > 1
                    ? COPY.PARTS_ANSWERED(answerParts.answered, answerParts.total)
                    : COPY.ANSWER_TO_CONTINUE}
                </span>
              )}
              <button
                type="button"
                className={styles.engDiagSubmit}
                onClick={() => void submit()}
                disabled={busy || !canSubmit}
                title={canSubmit ? undefined : COPY.ANSWER_BEFORE_SUBMIT}
              >
                {busy ? COPY.SUBMITTING : COPY.SUBMIT_ANSWER}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </EngAudioFetcherContext.Provider>
  )
}

export default EngSkillBuilderPlayer
